import React, { useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

const CONFIG = {
  "personal-lpoa": {
    title: "Personal Authorization, Consent & Limited Power of Attorney",
    intro:
      "By completing and signing below, you authorize DAFARGOS HOLDINGS LLC to act on your behalf for the services you requested, grant a limited power of attorney (no financial obligations), and consent to release of information. Electronic signatures are legally binding under the E-SIGN Act. Governed by the laws of the State of Georgia.",
    fields: [
      { name: "client_name", label: "Client Name", required: true },
      { name: "address", label: "Address" },
      { name: "phone", label: "Phone" },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "dob", label: "Date of Birth", type: "date" },
    ],
    acks: [
      "The information I provide is true.",
      "I understand DAFARGOS HOLDINGS LLC cannot guarantee any specific outcome.",
      "I understand results depend on third parties.",
      "I authorize communication by phone, email, mail, and text message.",
    ],
  },
  "business-lpoa": {
    title: "Business Authorization, Corporate Consent & Limited Power of Attorney",
    intro:
      "By completing and signing below, you certify you are authorized to bind the business and authorize DAFARGOS HOLDINGS LLC to represent it for the requested services, with a limited power of attorney (no borrowing, asset transfers, or tax filings unless separately authorized). No outcomes are guaranteed. Governed by the laws of the State of Georgia.",
    fields: [
      { name: "effective_date", label: "Effective Date", type: "date" },
      { name: "business_name", label: "Business Name", required: true },
      { name: "business_address", label: "Business Address" },
      { name: "entity_type", label: "Entity Type", type: "select", options: ["LLC", "Corporation", "Partnership", "Sole Proprietorship"] },
      { name: "authorized_representative", label: "Authorized Representative", required: true },
      { name: "title", label: "Title" },
      { name: "email", label: "Email", type: "email", required: true },
    ],
    acks: [
      "I certify I am legally authorized to bind this business.",
      "I understand no approval, funding, or outcome is guaranteed.",
      "I agree to the indemnification and release-of-information terms above.",
    ],
  },
  "credit-hero": {
    custom: true,
    title: "Credit Hero — Sign Up & Submit Your Info",
    intro:
      "Step 1: create your Credit Hero account using our partner link below. Step 2: come back here and submit your Credit Hero login and a copy of your credit report so DAFARGOS HOLDINGS LLC can begin working your file.",
    signupUrl: "https://creditheroscore.com/redirect.asp?guid=IK42ZBH6B828&sid=ZSFHSQHP2",
    fields: [
      { name: "name", label: "Your Name", required: true },
      { name: "email", label: "Email", type: "email", required: true },
      { name: "ch_login", label: "Credit Hero Login (username or email)", required: true },
      { name: "ch_password", label: "Credit Hero Password", required: true },
    ],
  },
};

export default function PublicForm() {
  const { kind } = useParams();
  const [sp] = useSearchParams();
  const cfg = CONFIG[kind];
  const [form, setForm] = useState(() => ({ email: sp.get("email") || "" }));
  const [acks, setAcks] = useState({});
  const [signature, setSignature] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);

  if (!cfg) {
    return <div className="min-h-screen flex items-center justify-center bg-background text-foreground">Form not found.</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    for (const f of cfg.fields) {
      if (f.required && !String(form[f.name] || "").trim()) { toast.error(`${f.label} is required`); return; }
    }
    if (cfg.custom) {
      setSaving(true);
      try {
        const fd = new FormData();
        cfg.fields.forEach((f) => fd.append(f.name, form[f.name] || ""));
        if (file) fd.append("file", file);
        await api.post(`/forms/${kind}/submit`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setDone(true);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
      } finally { setSaving(false); }
      return;
    }
    if (!cfg.acks.every((_, i) => acks[i])) { toast.error("Please acknowledge all items"); return; }
    if (!signature.trim()) { toast.error("Please type your signature"); return; }
    setSaving(true);
    try {
      await api.post(`/forms/${kind}/submit`, {
        data: { ...form, acknowledgements: cfg.acks.filter((_, i) => acks[i]) },
        signature: signature.trim(),
      });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submission failed");
    } finally { setSaving(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4" data-testid="form-success">
        <div className="max-w-md text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-400 mb-4" />
          <h1 className="font-display text-2xl tracking-tighter">Submitted & signed</h1>
          <p className="text-muted-foreground mt-2">Your signed authorization has been sent to DAFARGOS HOLDINGS LLC and a copy emailed to you.</p>
          <Link to="/" className="inline-block mt-6 text-primary underline">Return home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="public-form-page">
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="overline text-primary">DAFARGOS HOLDINGS LLC</div>
        <h1 className="font-display text-2xl sm:text-3xl tracking-tighter mt-1">{cfg.title}</h1>
        <p className="text-sm text-muted-foreground mt-4 leading-relaxed">{cfg.intro}</p>

        {cfg.custom && cfg.signupUrl && (
          <a href={cfg.signupUrl} target="_blank" rel="noreferrer" data-testid="credit-hero-signup"
             className="inline-flex items-center gap-2 mt-6 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-medium hover:bg-[#DBC197] transition-colors">
            Step 1 · Sign up on Credit Hero →
          </a>
        )}

        <div className="mt-8 space-y-4">
          {cfg.fields.map((f) => (
            <div key={f.name}>
              <Label>{f.label}{f.required && " *"}</Label>
              {f.type === "select" ? (
                <Select value={form[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger data-testid={`form-${f.name}`}><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              ) : f.name === "address" || f.name === "business_address" ? (
                <Textarea data-testid={`form-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
              ) : (
                <Input data-testid={`form-${f.name}`} type={f.type || "text"} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} />
              )}
            </div>
          ))}
        </div>

        {cfg.custom && (
          <div className="mt-6">
            <Label>Upload your credit report (PDF or image)</Label>
            <Input type="file" data-testid="form-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-zinc-900/60 border-white/10" />
          </div>
        )}

        {!cfg.custom && (
          <div className="mt-8 space-y-3 rounded-xl border border-white/10 bg-zinc-900/50 p-5">
            <div className="text-sm font-medium mb-1">Acknowledgements</div>
            {cfg.acks.map((a, i) => (
              <label key={i} className="flex items-start gap-3 text-sm cursor-pointer">
                <Checkbox data-testid={`form-ack-${i}`} checked={!!acks[i]} onCheckedChange={(v) => setAcks((s) => ({ ...s, [i]: !!v }))} className="mt-0.5" />
                <span>{a}</span>
              </label>
            ))}
          </div>
        )}

        {!cfg.custom && (
          <div className="mt-8">
            <Label>Type your full legal name as your electronic signature *</Label>
            <Input data-testid="form-signature" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Your full legal name"
              className="font-serif italic text-lg" />
            <p className="text-xs text-muted-foreground mt-2">Electronic signatures have the same legal effect as handwritten signatures (E-SIGN Act).</p>
          </div>
        )}

        <Button data-testid="form-submit" onClick={submit} disabled={saving}
          className="mt-8 w-full bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
          {saving ? "Submitting…" : (cfg.custom ? "Submit My Info" : "Sign & Submit")}
        </Button>
      </div>
    </div>
  );
}
